import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { CheckCircle } from 'lucide-react'
import { Button, Input, Label } from '@/components/ui'
import { useAuth } from '@/hooks'
import { forgotPasswordSchema, type ForgotPasswordFormData } from '@/lib/validations'

export function ForgotPasswordPage() {
  const { resetPassword, isLoading, error, clearError } = useAuth()
  const [emailSent, setEmailSent] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    clearError()
    try {
      await resetPassword(data.email)
      setEmailSent(true)
    } catch {
      // Error is handled by useAuth
    }
  }

  if (emailSent) {
    return (
      <div className="text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success-50">
          <CheckCircle className="h-6 w-6 text-success-600" />
        </div>
        <h2 className="mt-4 text-lg font-semibold text-gray-900">Check your email</h2>
        <p className="mt-2 text-sm text-gray-600">
          We&apos;ve sent a password reset link to{' '}
          <span className="font-medium">{getValues('email')}</span>
        </p>
        <p className="mt-4 text-sm text-gray-500">
          Didn&apos;t receive the email? Check your spam folder or{' '}
          <button
            onClick={() => setEmailSent(false)}
            className="font-medium text-primary-600 hover:text-primary-500"
          >
            try again
          </button>
        </p>
        <Link
          to="/login"
          className="mt-6 inline-block text-sm font-medium text-primary-600 hover:text-primary-500"
        >
          Back to sign in
        </Link>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-serif font-semibold text-gray-900">Forgot your password?</h2>
        <p className="mt-2 text-sm text-gray-600">
          No worries! Enter your email and we&apos;ll send you a link to reset your password.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-error-50 p-4">
          <p className="text-sm text-error-700">{error}</p>
        </div>
      )}

      <div>
        <Label htmlFor="email">Email address</Label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          className="mt-1"
          {...register('email')}
        />
        {errors.email && <p className="mt-1 text-sm text-error-600">{errors.email.message}</p>}
      </div>

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? 'Sending...' : 'Send reset link'}
      </Button>

      <p className="text-center text-sm text-gray-600">
        <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
          Back to sign in
        </Link>
      </p>
    </form>
  )
}
